import { MouseEvent, useMemo, useRef } from "react";

import { Link } from "../link";
import { ResponsiveValue } from "../../types";
import { Stack, Theme } from "@vinomofo/components";
import styled from "styled-components";
import { useInstantSearch } from "../../hooks/use-instantsearch";
import { useLocation } from "@reach/router";
import useWindowSize from "../../hooks/use-window-size";
import { storefrontNavigationItemProps } from "../../types/vino-data-types";
import { navigate } from "gatsby";

export interface HeaderCategoryNavProps {
  fontSize?: ResponsiveValue<number>;
  menuItems?: storefrontNavigationItemProps[];
}

const NavLink = styled(Link)<{
  isActive?: boolean;
  theme: Theme;
}>`
  display: inline-block;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.mofoPrimary : null};
  &:hover {
    color: ${({ theme }) => theme.colors.mofoPrimary};
  }
`;

const StyledNav = styled.nav<{ withShadow?: boolean }>`
  padding: 0 5px;
  margin: 0;
  list-style: none;
  display: flex;
  justify-content: space-between;
  width: 100%;
  overflow-y: hidden;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
  // position: relative;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    &::before {
      content: "";
      position: absolute;
      right: -1px;
      width: 70px;
      background: linear-gradient(to right, rgba(17, 17, 17, 0) 0, #111 100%);
      pointer-events: none;
      opacity: 1;
      transition: opacity 0.3s ease-in-out;
      z-index: 0;
      height: 15px;
      display: ${({ withShadow }) => (withShadow ? "block" : "none")};
    }
  }
`;

const StyledStack = styled(Stack)`
  width: 100%;
  justify-content: space-between;

  & > div {
    margin-left: 0;
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    & > div:not(:first-child) {
      margin-left: 15px;
    }
  }
`;

export const HeaderCategoryNav = ({
  fontSize = 0,
  menuItems,
}: HeaderCategoryNavProps): JSX.Element => {
  const styledNavRef = useRef(null);
  const LOCATION = useLocation();
  const { pathname, search } = LOCATION;
  const [screeWidth] = useWindowSize();
  const { carryOverFilter } = useInstantSearch();
  const savedFilter = (carryOverFilter || search).replace(
    /(\?|&)(query|page)=[^&]+/g,
    ""
  );

  const navigatePage = (
    event: MouseEvent<HTMLAnchorElement, MouseEvent>,
    url: string
  ) => {
    event.preventDefault();
    navigate(url);
  };

  const MenuList = useMemo(() => {
    if (!menuItems) return [];

    const noFilterLink = ["vim", "wine clubs", "articles"];

    return menuItems.map((item, index) => {
      const filteredLink = noFilterLink.includes(item.label.toLowerCase())
        ? item.link
        : item.link.endsWith("/")
        ? `${item.link}${savedFilter}`
        : `${item.link}/${savedFilter}`;
      return (
        <NavLink
          fontSize={fontSize}
          to={filteredLink}
          onClick={(event) => navigatePage(event, filteredLink)}
          color="white"
          isExternal
          key={index}
          isActive={
            pathname === (item.link.endsWith("/") ? item.link : `${item.link}/`)
          }
        >
          {item.label}
        </NavLink>
      );
    });
  }, [menuItems, LOCATION, carryOverFilter]);

  let withShadow = false;
  if (styledNavRef.current && screeWidth < styledNavRef.current.scrollWidth)
    withShadow = true;

  return (
    <StyledNav ref={styledNavRef} withShadow={withShadow}>
      <StyledStack space={[3, 3, 4]} horizontal>
        {MenuList}
      </StyledStack>
    </StyledNav>
  );
};
